import { IMA_VIDEO_LOAD_TIMEOUT } from '../constants/video'
import { PlayerState } from '../enums/PlayerState'
import Logger from '../helpers/Logger'
import { IMAFacade, IVideoAdConfig } from '../ImaFacade/ImaFacade'
import { AdPlayer, IPlayerConfig } from './AdPlayer'

export class ImaPlayer extends AdPlayer {
  /** Becomes available asynchronously after ads have been requested */
  adsManager?: google.ima.AdsManager
  adDisplayContainer: google.ima.AdDisplayContainer
  adsLoader: google.ima.AdsLoader
  adConfig: IVideoAdConfig

  static async new(config: IPlayerConfig) {
    IMAFacade.enableVPAID()
    const player = new ImaPlayer(config)
    player.adDisplayContainer = await IMAFacade.getDisplayContainer(
      config.container
    )
    player.adsLoader = await IMAFacade.getAdsLoader(player.adDisplayContainer)
    player.addLoaderEvents()

    return player
  }

  private constructor(protected readonly config: IPlayerConfig) {
    super(config)
  }

  async initVideoAd({
    vastXml,
    vastUrl
  }: RequireOnlyOne<{ vastXml; vastUrl }>) {
    this.cleanup()
    const { autoplay, muted, height, width } = this.config

    const adsRequest = await IMAFacade.getAdsRequest({
      vastXml,
      vastUrl,
      autoplay,
      height,
      width,
      muted
    })

    this._playerState = PlayerState.init
    this.adsLoader.requestAds(adsRequest)
  }

  play() {
    if (this._playerState === PlayerState.init) {
      this.adsManager?.start()
      this._playerState = PlayerState.start
    } else {
      this.adsManager?.resume()
      this._playerState = PlayerState.resume
    }
  }

  pause() {
    this.adsManager?.pause()
    this._playerState = PlayerState.pause
  }

  cleanup() {
    if (this.adsManager) {
      this.adsManager.destroy()
    }
    this.muteButton.style.display = 'none'
  }

  protected handleMuteClick() {
    super.handleMuteClick()
    const currentVolume = this.adsManager?.getVolume()
    if (currentVolume === 0) {
      const volumeValue = 0.8
      this.adsManager?.setVolume(volumeValue)
    } else {
      this.adsManager?.setVolume(0)
    }
  }

  private addLoaderEvents() {
    this.adsLoader.addEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      (event) => this.eventManager.trigger(this.events.adError, event),
      false
    )

    this.adsLoader.addEventListener(
      window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      this.onAdsManagerLoadedHandler,
      false
    )
  }

  private onAdsManagerLoadedHandler = this.onAdsManagerLoaded.bind(this)
  private onAdsManagerLoaded(
    adsManagerLoadedEvent: google.ima.AdsManagerLoadedEvent
  ) {
    const adsRenderingSettings = new window.google.ima.AdsRenderingSettings()
    adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true
    adsRenderingSettings.uiElements = []
    adsRenderingSettings.loadVideoTimeout = IMA_VIDEO_LOAD_TIMEOUT
    const videoContentSpoof = {
      currentTime: 0,
      duration: 10
    }
    this.adsManager = adsManagerLoadedEvent.getAdsManager(
      videoContentSpoof,
      adsRenderingSettings
    )

    this.initAdsManager(this.adsManager)
  }

  private initAdsManager(adsManager: google.ima.AdsManager) {
    const { muted } = this.config

    if (muted) {
      adsManager?.setVolume(0)
      super.mute()
    }

    this.addAdListeners(adsManager)

    try {
      const { height, width } = this.config
      // Initialize the ads manager. Ad rules playlist will start at this time.
      adsManager.init(width, height, window.google.ima.ViewMode.NORMAL)

      this.muteButton.style.display = 'block'
      this.play()
    } catch (adError) {
      /**  An error may be thrown if there was a problem with the VAST response. */
      Logger.debug('playAds Error: ', adError)
    }
  }

  private addAdListeners(adsManager: google.ima.AdsManager) {
    adsManager.addEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      (event) => this.eventManager.trigger(this.events.adError, event)
    )

    adsManager.addEventListener(
      window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
      (event) => this.eventManager.trigger(this.events.adsCompleted, event)
    )

    // Listen to any additional events, if necessary.
    adsManager.addEventListener(
      window.google.ima.AdEvent.Type.STARTED,
      (event) => this.eventManager.trigger(this.events.started, event)
    )

    adsManager.addEventListener(
      window.google.ima.AdEvent.Type.IMPRESSION,
      (event) => this.eventManager.trigger(this.events.impression, event)
    )
  }
}
